import { isAxiosError } from "axios";
import { useFormContext } from "react-hook-form";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ArrowLeftIcon, TagIcon, TruckIcon } from "@heroicons/react/24/outline";
import Header from "../../components/layout/Header";
import LoadingScreen from "../../components/loadingScreen";
import { useApplyDiscountCode } from "../../hooks/useApplyDiscountCode";
import { useUserContext } from "../../hooks/useUserContext";
import { useShippingLabelContext } from "../../hooks/useShippingLabelContext";
import { routeEndpoints } from "../../routes/routeEndpoints";
import type { ShippingLabelFormValues } from "../../types/forms";
import DiscountCode from "./components/discountCode";

export default function ReviewPurchasePage() {
  const { isLoading } = useUserContext();
  const navigate = useNavigate();
  const {
    mutate: applyDiscountCode,
    data: appliedDiscount,
    reset: removeDiscount,
    isPending: isApplyingDiscount,
    error: discountError,
  } = useApplyDiscountCode();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const { getValues } = useFormContext<ShippingLabelFormValues>();
  const {
    ratesQuery: { data: rates },
  } = useShippingLabelContext();

  const { packageDetails, rateId } = getValues();
  const rate = rates?.find(({ id }) => id === rateId);
  const shippingLabelLocation = {
    pathname: routeEndpoints.shippingLabel,
    search,
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!rate) {
    return <Navigate to={shippingLabelLocation} replace />;
  }

  const appliedCode = appliedDiscount?.code ?? null;
  const discount = appliedDiscount?.discountAmount ?? 0;
  const total = appliedDiscount?.total ?? rate.price;
  const discountErrorMessage = discountError
    ? isAxiosError(discountError) && discountError.response?.status === 404
      ? "This discount code is invalid."
      : "Couldn't apply the discount code. Please try again."
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB] font-sans">
      <Header />
      <main className="flex-1 px-4 md:px-0 py-6 md:py-5">
        <div className="max-w-200 w-full mx-auto flex flex-col gap-5">
          <button
            type="button"
            onClick={() => navigate(shippingLabelLocation)}
            className="flex w-fit items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
          >
            <ArrowLeftIcon className="size-4" />
            Back
          </button>

          <div className="w-full border border-gray-200 rounded-xl bg-white md:p-5 px-4 py-6 md:mb-20 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-50">
                <TruckIcon className="size-5 text-blue-600" />
              </div>
              <h1 className="text-xl md:text-2xl font-medium text-gray-950">
                Review & Purchase
              </h1>
            </div>

            <div className="flex flex-col text-sm">
              <SummaryRow label="Order ID">{searchParams.get("id")}</SummaryRow>
              <SummaryRow label="Package Type">{rate.packaging}</SummaryRow>
              <SummaryRow label="Weight">
                {packageDetails.weight} lbs
              </SummaryRow>
              <SummaryRow label="Dimensions (in)">
                {packageDetails.length} × {packageDetails.width} ×{" "}
                {packageDetails.height}
              </SummaryRow>
              <SummaryRow label="Carrier">{rate.carrier}</SummaryRow>
              <SummaryRow label="Service">{rate.service}</SummaryRow>
              <SummaryRow label="Delivery Estimate">
                {rate.deliveryEstimate}
              </SummaryRow>
              <SummaryRow label="Subtotal">${rate.price.toFixed(2)}</SummaryRow>
              <SummaryRow
                label={
                  <span className="flex items-center gap-2">
                    Discount
                    {appliedCode && (
                      <span className="flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700">
                        <TagIcon className="size-3" />
                        {appliedCode}
                      </span>
                    )}
                  </span>
                }
              >
                <span className="text-red-500">
                  {discount > 0
                    ? `− $${discount.toFixed(2)}`
                    : `$${discount.toFixed(2)}`}
                </span>
              </SummaryRow>
              <div className="flex items-center justify-between gap-4 py-3.5">
                <span className="text-base font-medium text-gray-950">
                  Total
                </span>
                <span className="text-xl font-semibold text-blue-600">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mt-4">
              <DiscountCode
                appliedCode={appliedCode}
                discount={discount}
                isApplying={isApplyingDiscount}
                error={discountErrorMessage}
                onApply={(code) => applyDiscountCode({ code, rateId: rate.id })}
                onRemove={removeDiscount}
              />
            </div>

            <button
              type="button"
              className="mt-5 h-13 w-full rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
            >
              Purchase
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

const SummaryRow = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-4 border-b border-gray-100 py-3.5">
    <span className="shrink-0 text-gray-500">{label}</span>
    <span className="text-right text-gray-950 break-all">{children}</span>
  </div>
);
